import React, { useState, useEffect, useMemo } from "react";
import { SYMBOL_TO_ID } from "../dashboard/UseLivemarket";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { apiConnectorGet, apiConnectorPost } from "../../services/apiconnector";
import { endpoint } from "../../services/urls";
import { useSocket } from "../../shared/SocketContext";

const coinAccent = {
  BTCUSDT: "linear-gradient(90deg,#f7931a,#ffb347)",
  ETHUSDT: "linear-gradient(90deg,#627eea,#a0b4f8)",
  SOLUSDT: "linear-gradient(90deg,#9945ff,#14f195)",
  BNBUSDT: "linear-gradient(90deg,#f3ba2f,#f0d060)",
  ADAUSDT: "linear-gradient(90deg,#0033ad,#3b82f6)",
  AVAXUSDT: "linear-gradient(90deg,#e84142,#ff7b7c)",
  LINKUSDT: "linear-gradient(90deg,#2a5ada,#4d88ff)",
  DOTUSDT: "linear-gradient(90deg,#e6007a,#ff6bbd)",
};

function normalizeRow(row, index) {
  const pair = row.tr15_pair ?? "UNKNOWN";
  const base = pair.replace("USDT", "");
  return {
    id: row.tr15_id ?? index + 1,
    pair,
    symbol: pair,
    coin: `${base}/USDT`,
    tradeType: row.tr15_trade_type ?? "–",
    entryPrice: Number(row.tr15_current_price ?? 0),
    lotSize: Number(row.tr15_lot_size ?? 0),
    leverage: Number(row.tr15_leverage ?? 1),
    timeFrame: Number(row.tr15_time_frame ?? 1),
    status: row.tr15_trade_staus ?? row.tr15_trade_status ?? "Pending",
    profit: Number(row.tr15_profit ?? 0),
    exitPrice: Number(row.tr15_exit_price ?? 0),
    entryTime: row.tr15_entry_time ? new Date(row.tr15_entry_time) : null,
    exitTime: row.tr15_exit_time ? new Date(row.tr15_exit_time) : null,
  };
}

const COIN_LOGO = (symbol) => {
  const id = SYMBOL_TO_ID[symbol];
  return id
    ? `https://assets.coincap.io/assets/icons/${id}@2x.png`
    : `https://cryptoicons.org/api/color/${symbol.toLowerCase()}/64`;
};

function CoinAvatar({ symbol }) {
  const [failed, setFailed] = useState(false);
  const fallbackBgs = [
    "bg-cyan-800",
    "bg-emerald-800",
    "bg-violet-800",
    "bg-amber-800",
    "bg-rose-800",
    "bg-blue-800",
    "bg-green-800",
    "bg-orange-800",
  ];
  const bg = fallbackBgs[symbol.charCodeAt(0) % fallbackBgs.length];
  if (failed) {
    return (
      <div
        className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 border border-cyan-900/30 text-white font-bold text-sm font-mono ${bg}`}
      >
        {symbol[0]}
      </div>
    );
  }
  return (
    <img
      src={COIN_LOGO(symbol)}
      alt={symbol}
      className="w-9 h-9 rounded-full bg-[#1a2030] border border-cyan-900/30 object-contain flex-shrink-0"
      onError={() => setFailed(true)}
    />
  );
}

const fmtPrice = (n) =>
  n >= 1000
    ? n.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : n < 0.01
      ? n.toFixed(4)
      : n.toFixed(4);

const fmtDate = (d) =>
  d
    ? d.toLocaleString("en-US", {
        month: "2-digit",
        day: "2-digit",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      })
    : "—";

// ── Main component ────────────────────────────────────────────────────────────

export default function MyTrades() {
  const [tab, setTab] = useState("bot");
  const [filter, setFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("date");
  const [page, setPage] = useState(1);

  const queryClient = useQueryClient();
  const socket = useSocket();

  useEffect(() => {
    const handleTick = (data) => {
      const parts = String(data).split(":");
      if (parts.length === 3 && Number(parts[2]) === 2) {
        queryClient.invalidateQueries({ queryKey: ["myTrades"] });
      }
    };
    socket.on("seconds", handleTick);
    return () => socket.off("seconds", handleTick);
  }, [socket, queryClient]);

  const { data: userData, isLoading: userLoading } = useQuery({
    queryKey: ["dashboard"],
    queryFn: () => apiConnectorGet(endpoint.member_details),
    enabled: !!endpoint?.member_details,
    refetchOnMount: false,
    refetchOnWindowFocus: true,
  });
  const user = userData?.data?.result?.[0] || {};

  const {
    data: tradesData = { rows: [], totalPages: 1 },
    isLoading: tradesLoading,
    isFetching: tradesFetching,
  } = useQuery({
    queryKey: ["myTrades", user?.lgn_cust_id, page],
    queryFn: async () => {
      const res = await apiConnectorPost(endpoint.my_trades, { page });
      const raw = res?.data;
      const rows = Array.isArray(raw?.result?.data)
        ? raw.result.data
        : Array.isArray(raw)
          ? raw
          : Array.isArray(raw?.result)
            ? raw.result
            : Array.isArray(raw?.data)
              ? raw.data
              : [];
      const totalPages = raw?.result?.totalPage ?? 1;
      return { rows: rows.map(normalizeRow), totalPages };
    },
    enabled: !!user?.lgn_cust_id,
    staleTime: 30 * 1000,
    keepPreviousData: true,
  });

  const trades = tradesData.rows;
  const serverTotalPages = tradesData.totalPages;

  const pageLoading = userLoading || tradesLoading || tradesFetching;

  const closedTrades = trades.filter((t) => t.status !== "Pending");
  const totalProfit = closedTrades.reduce((s, t) => s + t.profit, 0);
  const todayStr = new Date().toDateString();
  const todayProfit = closedTrades
    .filter((t) => t.exitTime && t.exitTime.toDateString() === todayStr)
    .reduce((s, t) => s + t.profit, 0);
  const wins = closedTrades.filter((t) => t.profit > 0).length;
  const winRate =
    closedTrades.length > 0
      ? ((wins / closedTrades.length) * 100).toFixed(0)
      : "0";
  const pending = trades.filter((t) => t.status === "Pending").length;

  const filtered = trades
    .filter((t) => {
      if (filter === "ALL") return true;
      if (filter === "PENDING") return t.status === "Pending";
      if (filter === "WIN") return t.profit > 0 && t.status !== "Pending";
      if (filter === "LOSS") return t.profit <= 0 && t.status !== "Pending";
      return true;
    })
    .sort((a, b) => (sortBy === "date" ? b.id - a.id : b.profit - a.profit));

  const totalPages = Math.max(1, serverTotalPages);
  const safePage = Math.min(page, totalPages);
  const paginated = filtered;

  return (
    <div>
      <div
        className="min-h-screen bg-[#040d12] text-[#00A0FF] px-4 pb-[110px] relative overflow-hidden"
        style={{ fontFamily: "'DM Sans', sans-serif" }}
      >
        <div
          className="fixed -top-28 -left-16 w-96 h-96 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(0,229,255,0.07) 0%, transparent 70%)",
          }}
        />
        <div
          className="fixed -bottom-24 -right-12 w-80 h-80 rounded-full pointer-events-none"
          style={{
            background:
              "radial-gradient(circle, rgba(0,180,200,0.05) 0%, transparent 70%)",
          }}
        />

        {/* Header */}
        <header className="max-w-[960px] bg-[#040d12] mx-auto pt-6 mb-6 flex flex-wrap justify-between items-end gap-4">
          <div>
            <p className="text-[10px] tracking-[0.2em] text-[#00A0FF]/60 mb-1.5 font-mono">
              TRADE HISTORY
            </p>
            <h1 className="text-2xl font-bold text-[#00A0FF] tracking-tight font-mono">
              My Trades
            </h1>
          </div>

          {/* Tab switcher */}
          <div className="flex gap-1 rounded-xl border border-cyan-400/10 p-1 bg-[#06121a]">
            <button
              onClick={() => {
                setTab("bot");
                setPage(1);
              }}
              className={`font-mono text-[11px] tracking-wide px-4 py-1.5 rounded-lg border transition-all cursor-pointer
                ${
                  tab === "bot"
                    ? "bg-[#00A0FF]/10 border-[#00A0FF]/40 text-[#00A0FF]"
                    : "border-transparent text-slate-500 hover:text-slate-300"
                }`}
            >
              🤖 Bot Trades
            </button>
            <button
              onClick={() => {
                setTab("self");
                setPage(1);
                setFilter("ALL");
              }}
              className={`font-mono text-[11px] tracking-wide px-4 py-1.5 rounded-lg border transition-all cursor-pointer
                ${
                  tab === "self"
                    ? "bg-[#00A0FF]/10 border-[#00A0FF]/40 text-[#00A0FF]"
                    : "border-transparent text-slate-500 hover:text-slate-300"
                }`}
            >
              My Trades
            </button>
          </div>

          {tab === "self" && (
            <div className="w-full grid grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-2.5">
              <StatPill
                label="Total P&L"
                value={`${totalProfit >= 0 ? "+" : "–"}$${fmtPrice(Math.abs(totalProfit))}`}
                color={totalProfit >= 0 ? "emerald" : "red"}
              />
              <StatPill
                label="Today's P&L"
                value={`${todayProfit >= 0 ? "+" : "–"}$${fmtPrice(Math.abs(todayProfit))}`}
                color={todayProfit >= 0 ? "emerald" : "red"}
              />
              <StatPill
                label="Win Rate"
                value={`${winRate}%`}
                color={Number(winRate) >= 50 ? "emerald" : "red"}
              />
              {/* <StatPill label="Closed" value={String(closedTrades.length)} color="cyan" /> */}
              <StatPill label="Pending" value={String(pending)} color="amber" />
            </div>
          )}
        </header>

        {tab === "bot" ? (
          <BotTradesView />
        ) : (
          <>
            {/* Controls */}
            <div className="max-w-[960px] mx-auto mb-5 flex flex-wrap items-center justify-center gap-3">
              <div className="flex gap-1">
                {["ALL", "WIN", "LOSS", "PENDING"].map((f) => (
                  <FilterBtn
                    key={f}
                    active={filter === f}
                    onClick={() => {
                      setFilter(f);
                      setPage(1);
                    }}
                  >
                    {f}
                  </FilterBtn>
                ))}
              </div>
              <div className="flex items-center gap-1">
                <span className="text-[11px] text-slate-500 tracking-wide font-mono mr-1">
                  Sort:
                </span>
                {[
                  ["date", "Date"],
                  ["profit", "P&L"],
                ].map(([v, l]) => (
                  <FilterBtn
                    key={v}
                    active={sortBy === v}
                    onClick={() => setSortBy(v)}
                  >
                    {l}
                  </FilterBtn>
                ))}
              </div>
            </div>

            {/* Loader */}
            {pageLoading && (
              <div
                className="max-w-[960px] mx-auto grid gap-4"
                style={{
                  gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))",
                }}
              >
                {[...Array(6)].map((_, i) => (
                  <div
                    key={i}
                    className="h-52 rounded-2xl bg-cyan-400/5 border border-cyan-400/10 animate-pulse"
                  />
                ))}
              </div>
            )}

            {/* Empty state */}
            {!pageLoading && filtered.length === 0 && (
              <div className="max-w-[960px] mx-auto flex flex-col items-center justify-center py-20 gap-3">
                <div className="w-14 h-14 rounded-full bg-cyan-400/5 border border-cyan-400/10 flex items-center justify-center">
                  <span className="font-mono text-2xl text-cyan-400/30">∅</span>
                </div>
                <p className="font-mono text-[11px] tracking-[0.15em] text-cyan-400/30 uppercase">
                  No trades found
                </p>
              </div>
            )}

            {/* Cards grid */}
            {!pageLoading && filtered.length > 0 && (
              <>
                <div
                  className="max-w-[960px] mx-auto grid gap-4"
                  style={{
                    gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))",
                  }}
                >
                  {paginated.map((t, i) => (
                    <TradeCard key={t.id} trade={t} index={i} />
                  ))}
                </div>

                <Pagination
                  page={safePage}
                  totalPages={totalPages}
                  onChange={setPage}
                />
                <p className="text-center font-mono text-[10px] text-slate-600 mt-3">
                  Page {safePage} of {totalPages}
                </p>
              </>
            )}
          </>
        )}

        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Space+Mono:wght@400;700&family=DM+Sans:wght@300;400;500;600&display=swap');
          .trade-card { opacity:0; transform:translateY(16px); animation:tradeIn 0.45s cubic-bezier(0.22,1,0.36,1) forwards; }
          .trade-card:hover { transform:translateY(-3px) !important; box-shadow:0 12px 40px rgba(0,229,255,0.12),0 0 0 1px rgba(0,229,255,0.18) !important; transition:all 0.2s ease !important; }
          @keyframes tradeIn { to { opacity:1; transform:translateY(0); } }
        `}</style>
      </div>
    </div>
  );
}

/* ── Bot Trades View ─────────────────────────────────────────────────────── */
function BotTradesView() {
  const [filter, setFilter] = useState("ALL");
  const [sortBy, setSortBy] = useState("date");
  const [pkgFilter, setPkgFilter] = useState("ALL");
  const [page, setPage] = useState(1);

  const {
    data: botData = { rows: [], totalPages: 1, summary: {} },
    isLoading,
    isFetching,
  } = useQuery({
    queryKey: ["botTrades", page],
    queryFn: async () => {
      const res = await apiConnectorGet(
        `${endpoint.get_bot_trades}?page=${page}`,
      );
      const result = res?.data?.result;
      const rows = Array.isArray(result?.data) ? result.data : [];
      return {
        rows,
        totalPages: result?.totalPage ?? 1,
        summary: result?.summary ?? {},
        totalRoi: result?.total_roi ?? result?.summary?.total_roi ?? 0,
        totalRoiCandidate:
          result?.total_roi_this_candidate ??
          result?.summary?.total_roi_this_candidate ??
          0,
      };
    },
    staleTime: 30 * 1000,
    keepPreviousData: true,
  });

  const rows = botData.rows;
  const totalPages = Math.max(1, botData.totalPages);
  const safePage = Math.min(page, totalPages);
  const loading = isLoading || isFetching;

  // Stats — server-computed across all pages, not just the current one
  const totalPnl = Number(botData.summary.totalPnl) || 0;
  const todayPnl = Number(botData.summary.todayPnl) || 0;
  const total = Number(botData.summary.totalTrades) || 0;
  const todayTrades = Number(botData.summary.todayTrades) || 0;
  const winRate = Number(botData.summary.winRate) || 0;
  const totalRoi = Number(botData.totalRoi) || 0;
  const totalRoiCandidate = Number(botData.totalRoiCandidate) || 0;

  // Unique packages for the package filter
  const packages = Array.from(
    new Map(
      rows.filter((r) => r.packageId).map((r) => [r.packageId, r.packageName]),
    ).entries(),
  ).sort((a, b) => a[0] - b[0]);

  const filtered = rows
    .filter((t) => {
      const pnl = parseFloat(t.pnl);
      if (filter === "WIN" && pnl <= 0) return false;
      if (filter === "LOSS" && pnl > 0) return false;
      if (pkgFilter !== "ALL" && String(t.packageId) !== pkgFilter)
        return false;
      return true;
    })
    .sort((a, b) => {
      if (sortBy === "pnl") return parseFloat(b.pnl) - parseFloat(a.pnl);
      if (sortBy === "pkg") return a.packageId - b.packageId;
      return b.id - a.id;
    });

  const { min, max } = useMemo(() => {
    const min = Math.floor(Math.random() * (30 - 15 + 1)) + 15; // 15-30
    const max = Math.floor(Math.random() * (35 - (min + 5) + 1)) + (min + 5); // min+5 to 35

    return { min, max };
  }, []);
  return (
    <>
      {/* Fund usage notice */}
      <div
        className="max-w-[960px] mx-auto mb-4 rounded-xl px-4 py-3 flex items-center gap-2.5"
        style={{
          background: "rgba(245,158,11,0.06)",
          border: "1px solid rgba(245,158,11,0.2)",
        }}
      >
        <span className="text-base">⚠️</span>
        <p className="text-[11px] text-amber-400/90 leading-relaxed">
          Your{" "}
          <span className="font-bold">
            {min}%–{max}%
          </span>{" "}
          of capital is used in bot trading.
        </p>
      </div>

      {/* Global Profit — highlighted */}
      <div
        className="max-w-[960px] mx-auto mb-4 rounded-2xl px-5 py-4 flex items-center justify-between gap-4"
        style={{
          background:
            "linear-gradient(90deg, rgba(0,160,255,0.14), rgba(16,185,129,0.06))",
          border: "1px solid rgba(0,160,255,0.35)",
          boxShadow: "0 0 28px rgba(0,160,255,0.12)",
        }}
      >
        <div>
          <p className="font-mono text-[10px] tracking-[0.15em] text-[#00A0FF] uppercase mb-1">
            🌐 Global Profit
          </p>
          <p className="text-[10px] text-slate-500">
            Total ROI earned across all bot trades
          </p>
        </div>
        <p
          className={`font-mono text-2xl font-bold ${totalRoi >= 0 ? "!text-emerald-400" : "!text-red-400"}`}
        >
          {totalRoi >= 0 ? "+" : "–"}${Math.abs(totalRoi).toFixed(2)}
        </p>
      </div>

      {/* Stats */}
      <div className="max-w-[960px] mx-auto mb-5 w-full grid grid-cols-2 sm:grid-cols-3 gap-2.5">
        <StatPill
          label="Total P&L"
          value={`${totalPnl >= 0 ? "+" : "–"}$${Math.abs(totalPnl).toFixed(5)}`}
          color={totalPnl >= 0 ? "emerald" : "red"}
        />
        <StatPill
          label="Today's P&L"
          value={`${todayPnl >= 0 ? "+" : "–"}$${Math.abs(todayPnl).toFixed(5)}`}
          color={todayPnl >= 0 ? "emerald" : "red"}
        />
        <StatPill label="Total Trades" value={String(total)} color="cyan" />
        <StatPill
          label="Today's Trades"
          value={String(todayTrades)}
          color="cyan"
        />
        <StatPill
          label="Win Rate"
          value={`${winRate}%`}
          color={winRate >= 50 ? "emerald" : "red"}
        />
        <StatPill
          label="Total Profit"
          value={`${totalRoiCandidate >= 0 ? "+" : "–"}$${Math.abs(totalRoiCandidate).toFixed(2)}`}
          color={totalRoiCandidate >= 0 ? "emerald" : "red"}
        />
      </div>

      {/* Controls */}
      <div className="max-w-[960px] mx-auto mb-5 flex flex-col gap-3">
        {/* P&L filter + sort */}
        <div className="flex flex-wrap items-center justify-center gap-3">
          <div className="flex gap-1">
            {["ALL", "WIN", "LOSS"].map((f) => (
              <FilterBtn
                key={f}
                active={filter === f}
                onClick={() => setFilter(f)}
              >
                {f}
              </FilterBtn>
            ))}
          </div>
          <div className="flex items-center gap-1">
            <span className="text-[11px] text-slate-500 tracking-wide font-mono mr-1">
              Sort:
            </span>
            {[["date", "Date"], ["pnl", "P&L"], ["pkg"]].map(([v, l]) => (
              <FilterBtn
                key={v}
                active={sortBy === v}
                onClick={() => setSortBy(v)}
              >
                {l}
              </FilterBtn>
            ))}
          </div>
        </div>
        {/* Package filter */}
        {packages.length > 1 && (
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            <span className="text-[11px] text-slate-500 tracking-wide font-mono mr-1">
              Plan:
            </span>
            <FilterBtn
              active={pkgFilter === "ALL"}
              onClick={() => setPkgFilter("ALL")}
            >
              All
            </FilterBtn>
            {packages.map(([id, name]) => (
              <FilterBtn
                key={id}
                active={pkgFilter === String(id)}
                onClick={() => setPkgFilter(String(id))}
              >
                {name}
              </FilterBtn>
            ))}
          </div>
        )}
      </div>

      {loading && (
        <div
          className="max-w-[960px] mx-auto grid gap-4"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))",
          }}
        >
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-44 rounded-2xl bg-cyan-400/5 border border-cyan-400/10 animate-pulse"
            />
          ))}
        </div>
      )}

      {!loading && filtered.length === 0 && (
        <div className="max-w-[960px] mx-auto flex flex-col items-center justify-center py-20 gap-3">
          <div className="w-14 h-14 rounded-full bg-cyan-400/5 border border-cyan-400/10 flex items-center justify-center">
            <span className="text-2xl">🤖</span>
          </div>
          <p className="font-mono text-[11px] tracking-[0.15em] text-cyan-400/30 uppercase">
            No bot trades found
          </p>
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <>
          <div
            className="max-w-[960px] mx-auto grid gap-4"
            style={{
              gridTemplateColumns: "repeat(auto-fill, minmax(280px,1fr))",
            }}
          >
            {filtered.map((t, i) => (
              <BotTradeCard key={t.id} trade={t} index={i} />
            ))}
          </div>
          <Pagination
            page={safePage}
            totalPages={totalPages}
            onChange={setPage}
          />
          <p className="text-center font-mono text-[10px] text-slate-600 mt-3">
            Page {safePage} of {totalPages}
          </p>
        </>
      )}
    </>
  );
}

/* ── Trade Card ──────────────────────────────────────────────────────────── */
function TradeCard({ trade: t, index }) {
  const isPending = t.status === "Pending";
  const isWin = !isPending && t.profit > 0;
  const isLoss = !isPending && !isWin;

  const statusColor = isPending
    ? "bg-amber-400/10 text-amber-400 border-amber-400/25"
    : isWin
      ? "bg-emerald-400/10 text-emerald-400 border-emerald-400/25"
      : "bg-red-400/10 text-red-400 border-red-400/25";

  const statusLabel = isPending ? "PENDING" : isWin ? "WIN" : "LOSS";

  return (
    <div
      className="trade-card rounded-2xl overflow-hidden border border-cyan-400/10 bg-[rgba(6,18,26,0.85)] backdrop-blur-md relative"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Accent bar */}
      <div
        className="h-[3px] w-full"
        style={{
          background:
            coinAccent[t.symbol] ?? "linear-gradient(90deg,#00e5ff,#0088aa)",
        }}
      />

      {/* Top row: coin + P&L */}
      <div className="flex justify-between items-start px-4 pt-3.5 pb-2.5">
        <div className="flex items-center gap-3">
          <CoinAvatar symbol={t.symbol} />
          <div>
            <p className="text-sm font-semibold text-slate-200 mb-0.5">
              {t.coin}
            </p>
            <div className="flex items-center gap-1.5">
              <span
                className={`font-mono text-[10px] px-2 py-px rounded border tracking-wider font-bold
                ${
                  t.tradeType === "CALL"
                    ? "bg-emerald-400/10 text-emerald-400 border-emerald-400/25"
                    : "bg-red-400/10 text-red-400 border-red-400/25"
                }`}
              >
                {t.tradeType === "CALL" ? "↑ CALL" : "↓ PUT"}
              </span>
              <span
                className={`font-mono text-[10px] px-2 py-px rounded border tracking-wider ${statusColor}`}
              >
                {statusLabel}
              </span>
            </div>
          </div>
        </div>

        {/* P&L */}
        <div className="text-right">
          {isPending ? (
            <p className="font-mono text-[13px] font-bold text-amber-400">
              In Progress
            </p>
          ) : (
            <>
              <p
                className={`font-mono text-[17px] font-bold ${isWin ? "!text-emerald-400" : "!text-red-400"}`}
              >
                {isWin ? "+" : "–"}${fmtPrice(Math.abs(t.profit))}
              </p>
              <p
                className={`font-mono text-[10px] mt-0.5 ${isWin ? "!text-emerald-400" : "!text-red-400"}`}
              >
                {isWin ? "▲ PROFIT" : "▼ LOSS"}
              </p>
            </>
          )}
        </div>
      </div>

      <div className="h-px mx-4 bg-cyan-400/[0.07]" />

      {/* Detail grid */}
      <div className="grid grid-cols-2 gap-2.5 px-4 py-3">
        <DetailCell
          label="Trade ID"
          value={`#${String(t.id).padStart(4, "0")}`}
          accent
        />
        <DetailCell label="Time Frame" value={`${t.timeFrame} min`} />
        <DetailCell label="Entry Price" value={`$${fmtPrice(t.entryPrice)}`} />
        <DetailCell
          label="Exit Price"
          value={t.exitPrice > 0 ? `$${fmtPrice(t.exitPrice)}` : "—"}
        />
        <DetailCell label="Lot Size" value={`$${fmtPrice(t.lotSize)}`} />
        <DetailCell label="Leverage" value={`${t.leverage}x`} />
        <DetailCell label="Entry Time" value={fmtDate(t.entryTime)} />
        <DetailCell label="Exit Time" value={fmtDate(t.exitTime)} />
      </div>
    </div>
  );
}

/* ── Bot Trade Card ──────────────────────────────────────────────────────── */
function BotTradeCard({ trade: t, index }) {
  const pnl = parseFloat(t.pnl);
  const isWin = pnl > 0;
  const symbol = t.coin?.replace("/", "") ?? "";

  const fmtClose = (str) => {
    if (!str) return "—";
    const d = new Date(str);
    return d.toLocaleString("en-US", {
      month: "2-digit",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  return (
    <div
      className="trade-card rounded-2xl overflow-hidden border border-cyan-400/10 bg-[rgba(6,18,26,0.85)] backdrop-blur-md relative"
      style={{ animationDelay: `${index * 60}ms` }}
    >
      {/* Accent bar */}
      <div
        className="h-[3px] w-full"
        style={{
          background:
            coinAccent[symbol] ?? "linear-gradient(90deg,#00e5ff,#0088aa)",
        }}
      />

      {/* Top row: coin + PnL */}
      <div className="flex justify-between items-start px-4 pt-3.5 pb-2.5">
        <div className="flex items-center gap-3">
          <CoinAvatar symbol={symbol} />
          <div>
            <p className="text-sm font-semibold text-slate-200 mb-0.5">
              {t.coin}
            </p>
            <div className="flex items-center gap-1.5">
              <span
                className={`font-mono text-[10px] px-2 py-px rounded border tracking-wider font-bold
                ${
                  t.TYPE === "Buy"
                    ? "bg-emerald-400/10 text-emerald-400 border-emerald-400/25"
                    : "bg-red-400/10 text-red-400 border-red-400/25"
                }`}
              >
                {t.TYPE === "Buy" ? "↑ BUY" : "↓ SELL"}
              </span>
              <span
                className={`font-mono text-[10px] px-2 py-px rounded border tracking-wider
                ${
                  t.POSITION === "Close"
                    ? "bg-slate-400/10 text-slate-400 border-slate-400/20"
                    : "bg-amber-400/10 text-amber-400 border-amber-400/25"
                }`}
              >
                {t.POSITION}
              </span>
            </div>
          </div>
        </div>

        {/* PnL */}
        <div className="text-right">
          <p
            className={`font-mono text-[17px] font-bold ${isWin ? "!text-emerald-400" : "!text-red-400"}`}
          >
            {isWin ? "+" : ""}${Math.abs(pnl).toFixed(5)}
          </p>
          <p
            className={`font-mono text-[10px] mt-0.5 ${isWin ? "!text-emerald-400" : "!text-red-400"}`}
          >
            {isWin ? "▲ PROFIT" : "▼ LOSS"}
          </p>
        </div>
      </div>

      <div className="h-px mx-4 bg-cyan-400/[0.07]" />

      {/* Detail grid */}
      <div className="grid grid-cols-2 gap-2.5 px-4 py-3">
        <DetailCell
          label="Trade ID"
          value={`#${String(t.id).padStart(4, "0")}`}
          accent
        />
        <DetailCell label="Package" value={t.packageName} />
        <DetailCell label="Closed At" value={fmtClose(t.closedAt)} />
        <DetailCell
          label="Transaction"
          value={t.TRANSACTION?.slice(-8) ?? "—"}
        />
      </div>

      {/* Package description tag */}
      <div className="px-4 pb-3">
        <span className="font-mono text-[9px] tracking-wide text-cyan-400/50 border border-cyan-400/10 rounded px-2 py-0.5">
          🤖 {t.packageDescription}
        </span>
      </div>
    </div>
  );
}

/* ── Sub-components ──────────────────────────────────────────────────────── */
function DetailCell({ label, value, accent }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-[9px] text-slate-600 tracking-[0.1em] uppercase">
        {label}
      </span>
      <span
        className={`text-xs font-medium truncate ${accent ? "text-cyan-400 font-mono" : "text-slate-400"}`}
      >
        {value}
      </span>
    </div>
  );
}

function StatPill({ label, value, color }) {
  const scheme = {
    cyan: "text-cyan-400    border-cyan-400/20    bg-cyan-400/[0.06]",
    emerald: "text-emerald-400 border-emerald-400/20 bg-emerald-400/[0.06]",
    red: "text-red-400     border-red-400/20     bg-red-400/[0.06]",
    amber: "text-amber-400   border-amber-400/20   bg-amber-400/[0.06]",
  };
  return (
    <div
      className={`flex flex-col items-center px-4 py-3 rounded-xl border ${scheme[color]}`}
    >
      <span className="text-[9px] tracking-[0.12em] text-slate-500 mb-0.5">
        {label}
      </span>
      <span className="font-mono text-[15px] font-bold">{value}</span>
    </div>
  );
}

function Pagination({ page, totalPages, onChange }) {
  if (totalPages <= 1) return null;
  const items =
    totalPages <= 4
      ? Array.from({ length: totalPages }, (_, i) => i + 1)
      : [1, 2, 3, "...", totalPages];

  return (
    <div className="max-w-[960px] mx-auto mt-6 flex items-center justify-center gap-2">
      <button
        onClick={() => onChange(Math.max(1, page - 1))}
        disabled={page === 1}
        className={`font-mono text-[11px] px-4 py-2 rounded-full border transition-all
          ${
            page === 1
              ? "border-cyan-400/10 text-slate-600 cursor-not-allowed"
              : "border-cyan-400/30 text-cyan-400 hover:bg-cyan-400/10 cursor-pointer"
          }`}
      >
        ← Prev
      </button>

      <div className="flex items-center gap-1">
        {items.map((p, idx) =>
          p === "..." ? (
            <span
              key={idx}
              className="font-mono text-[11px] text-slate-600 px-1 select-none"
            >
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              className={`font-mono text-[11px] w-8 h-8 rounded-full border transition-all cursor-pointer
                ${
                  p === page
                    ? "bg-[#00A0FF]/15 border-[#00A0FF]/50 text-[#00A0FF]"
                    : "border-cyan-400/10 text-slate-500 hover:border-cyan-400/25 hover:text-slate-300"
                }`}
            >
              {p}
            </button>
          ),
        )}
      </div>

      <button
        onClick={() => onChange(Math.min(totalPages, page + 1))}
        disabled={page === totalPages}
        className={`font-mono text-[11px] px-4 py-2 rounded-full border transition-all
          ${
            page === totalPages
              ? "border-cyan-400/10 text-slate-600 cursor-not-allowed"
              : "border-cyan-400/30 text-cyan-400 hover:bg-cyan-400/10 cursor-pointer"
          }`}
      >
        Next →
      </button>
    </div>
  );
}

function FilterBtn({ children, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`font-mono text-[11px] tracking-wide px-3.5 py-1 rounded-full border cursor-pointer transition-all duration-150
        ${
          active
            ? "bg-[#00A0FF]/10 border-[#00A0FF]/40 text-[#00A0FF]"
            : "bg-transparent border-[#005FCC]/15 text-slate-500 hover:text-slate-300 hover:border-[#005FCC]/25"
        }`}
    >
      {children}
    </button>
  );
}
